import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CreateFromSourceTemplateConfig } from '../template/MasterDocumentTemplate.types';
import { useMasterDocumentTranslation } from '../i18n';
import type { TranslateFn } from '../i18nFactory';
import type {
  AvailableSourceItem,
  CreateFromSourceSource,
  IMasterDocumentAdapter,
  PendingSourceLine,
} from '../adapter/MasterDocumentAdapter.types';

/** Modo de invocación. `create` = caso A (nace el documento); `append` = caso B (DRAFT existente). */
export type CreateFromSourceMode = 'create' | 'append';

/** Pasos del asistente. `origin` solo aplica en modo create con `allowFreeCapture`. */
export type CreateFromSourceStep = 'origin' | 'sources' | 'lines' | 'confirm';

/** Elección del selector de origen (paso 0). */
export type OriginChoice = 'source' | 'free';

export interface UseCreateFromSourceOptions {
  open: boolean;
  mode: CreateFromSourceMode;
  config: CreateFromSourceTemplateConfig;
  adapter: IMasterDocumentAdapter;
  /** Requerido en modo `append`. */
  documentId?: string;
  /** Centro de operación del contexto (header en caso A, del documento en caso B). */
  operationCenterId?: string;
  /**
   * Tercero fijado del header (modo `append`): filtra los orígenes y llega
   * read-only. En modo `create` es undefined (el usuario elige libremente).
   */
  lockedThirdPartyId?: string;
  /** Permisos del usuario. `null` ⇒ sin gating (mostrar todo). */
  userPermissions?: string[] | null;
  onCreated?: (documentId: string) => void;
  onAppended?: (addedLineIds: string[]) => void;
  onFreeCapture?: (route: string) => void;
}

/**
 * Validación de "Cantidad a cruzar" del asistente. Recibe `t` porque es una
 * función pura; el override de `config.i18n` sigue teniendo prioridad.
 */
function validateQuantity(
  quantity: number,
  pending: number,
  uom: string | undefined,
  i18n: CreateFromSourceTemplateConfig['i18n'],
  t: TranslateFn,
): string | undefined {
  if (Number.isNaN(quantity)) {
    return i18n?.quantityRequired ?? t('master_document.crossing.quantityRequired');
  }
  if (quantity <= 0) {
    return i18n?.quantityPositive ?? t('master_document.crossing.quantityPositive');
  }
  if (quantity > pending) {
    // El override conserva su contrato: `{uom}` sin espacio previo.
    const tpl = i18n?.quantityExceedsPending;
    if (tpl) {
      return tpl.replaceAll('{pending}', String(pending)).replaceAll('{uom}', uom ?? '').trim();
    }
    return t('master_document.crossing.quantityExceedsPending', {
      pending: String(pending),
      uom: uom ? ` ${uom}` : '',
    });
  }
  return undefined;
}

/** Lee una clave de compatibilidad de un origen (busca en el item y en `extra`). */
function readCompatKey(item: AvailableSourceItem, key: string): unknown {
  if (key in item) return (item as unknown as Record<string, unknown>)[key];
  return item.extra?.[key];
}

export interface UseCreateFromSourceResult {
  step: CreateFromSourceStep;
  originChoice: OriginChoice | null;

  candidates: AvailableSourceItem[];
  candidatesLoading: boolean;
  candidatesError?: string;
  selectedSourceIds: string[];
  /** sourceId → motivo de incompatibilidad (deshabilitado). Vacío ⇒ compatible. */
  incompatibleReasons: Record<string, string>;

  pendingLines: PendingSourceLine[];
  linesLoading: boolean;
  linesError?: string;
  selectedLineIds: string[];
  quantities: Record<string, number>;
  lineErrors: Record<string, string>;

  defaults: Record<string, unknown>;
  /** Valores de los campos de header (`createHeaderFields`) capturados en modo create. */
  headerValues: Record<string, unknown>;

  /** Opciones de catálogo cargadas por `defaultsStep.fields[].optionsEntity`. */
  selectOptions: Record<string, Array<{ label: string; value: string }>>;

  submitting: boolean;
  submitError?: string;

  canMultiSelect: boolean;
  canFreeCapture: boolean;
  canSeeFinancial: boolean;

  /** True cuando la selección actual permite avanzar del paso vigente. */
  canAdvance: boolean;
  /** True cuando existe el paso de confirmación. */
  hasConfirmStep: boolean;
  /** True cuando existe el paso 0 (selector de origen). */
  hasOriginStep: boolean;
  /** True cuando hay un paso anterior al que volver desde el paso vigente. */
  canGoBack: boolean;

  setOriginChoice: (choice: OriginChoice) => void;
  toggleSource: (sourceId: string) => void;
  toggleLine: (lineId: string, selected: boolean) => void;
  setQuantity: (lineId: string, quantity: number) => void;
  setDefault: (key: string, value: unknown) => void;
  setHeaderValue: (key: string, value: unknown) => void;

  next: () => void;
  back: () => void;
  submit: () => void;
}

/**
 * useCreateFromSource — orquesta el asistente "crear/continuar documento desde
 * origen(es)". Estado plano (sin React Query) para no exigir un
 * `QueryClientProvider` alrededor del flujo core de MasterDocument, igual que
 * `useCrossing`. Los datos se obtienen vía los fetchers inyectados en el adapter.
 */
export function useCreateFromSource(opts: UseCreateFromSourceOptions): UseCreateFromSourceResult {
  const {
    open, mode, config, adapter, documentId, operationCenterId, lockedThirdPartyId,
    userPermissions, onCreated, onAppended, onFreeCapture,
  } = opts;

  const { t } = useMasterDocumentTranslation();
  const i18n = config.i18n;
  const pendingField = config.lineSelection.pendingField ?? 'quantityPending';

  const canMultiSelect = useMemo(() => {
    const perm = config.multiSelect?.permission;
    if (!config.multiSelect) return false;
    if (!perm) return true;
    return userPermissions == null || userPermissions.includes(perm);
  }, [config.multiSelect, userPermissions]);

  const canFreeCapture = useMemo(() => {
    const perm = config.allowFreeCapture?.permission;
    if (!perm) return false;
    return userPermissions == null || userPermissions.includes(perm);
  }, [config.allowFreeCapture, userPermissions]);

  const canSeeFinancial = useMemo(() => {
    const perm = config.lineSelection.financialPermission;
    if (!perm) return true;
    return userPermissions == null || userPermissions.includes(perm);
  }, [config.lineSelection.financialPermission, userPermissions]);

  const hasOriginStep = mode === 'create' && !!config.allowFreeCapture;
  const hasConfirmStep = !!config.confirmationStep?.enabled;

  // ── State ───────────────────────────────────────────────
  const [step, setStep] = useState<CreateFromSourceStep>('sources');
  const [originChoice, setOriginChoiceState] = useState<OriginChoice | null>(null);

  const [candidates, setCandidates] = useState<AvailableSourceItem[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | undefined>();
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);

  const [pendingLines, setPendingLines] = useState<PendingSourceLine[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState<string | undefined>();
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});

  const [defaults, setDefaults] = useState<Record<string, unknown>>({});
  const [headerValues, setHeaderValues] = useState<Record<string, unknown>>({});
  const [selectOptions, setSelectOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  // ── Reset al abrir/cerrar ────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(hasOriginStep ? 'origin' : 'sources');
    setOriginChoiceState(null);
    setSelectedSourceIds([]);
    setPendingLines([]);
    setLinesError(undefined);
    setSelectedLineIds([]);
    setQuantities({});
    setLineErrors({});
    setDefaults({});
    setHeaderValues({});
    setSubmitError(undefined);
  }, [open, hasOriginStep]);

  // ── Fetch candidatos al entrar al paso 'sources' ─────────
  useEffect(() => {
    if (!open || step !== 'sources' || !adapter.fetchAvailableSources) return;
    let cancelled = false;
    setCandidatesLoading(true);
    setCandidatesError(undefined);
    adapter
      .fetchAvailableSources({
        childClass: config.childClass,
        thirdPartyId: lockedThirdPartyId,
        operationCenterId,
        states: config.eligibleSourceStates,
      })
      .then((items) => { if (!cancelled) setCandidates(items ?? []); })
      .catch((err) => { if (!cancelled) setCandidatesError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setCandidatesLoading(false); });
    return () => { cancelled = true; };
  }, [open, step, adapter, config.childClass, config.eligibleSourceStates, lockedThirdPartyId, operationCenterId]);

  // ── Carga opciones de catálogo para defaultsStep ─────────
  useEffect(() => {
    if (!open || !config.defaultsStep || !adapter.getSelectOptions) return;
    let cancelled = false;
    const entities = config.defaultsStep.fields
      .filter((f) => f.type === 'select' && f.optionsEntity)
      .map((f) => f.optionsEntity!);
    entities.forEach((entity) => {
      adapter
        .getSelectOptions!(entity, undefined, operationCenterId)
        .then((opts) => { if (!cancelled) setSelectOptions((prev) => ({ ...prev, [entity]: opts })); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [open, config.defaultsStep, adapter, operationCenterId]);

  // ── Compatibilidad multi-origen ──────────────────────────
  const incompatibleReasons = useMemo<Record<string, string>>(() => {
    const keys = config.compatibilityKeys ?? [];
    if (selectedSourceIds.length === 0 || keys.length === 0) return {};
    const anchor = candidates.find((c) => c.documentId === selectedSourceIds[0]);
    if (!anchor) return {};
    const result: Record<string, string> = {};
    for (const cand of candidates) {
      if (cand.documentId === anchor.documentId) continue;
      const diffs = keys.filter((k) => readCompatKey(cand, k) !== readCompatKey(anchor, k));
      if (diffs.length > 0) {
        const tpl =
          i18n?.incompatibleReason ?? t('master_document.createFromSource.incompatibleReason');
        result[cand.documentId] = tpl.replaceAll('{keys}', diffs.join(', '));
      }
    }
    return result;
  }, [candidates, selectedSourceIds, config.compatibilityKeys, i18n, t]);

  // ── Handlers ─────────────────────────────────────────────
  const setOriginChoice = useCallback((choice: OriginChoice) => {
    setOriginChoiceState(choice);
    if (choice === 'free') {
      if (config.allowFreeCapture) onFreeCapture?.(config.allowFreeCapture.route);
    } else {
      setStep('sources');
    }
  }, [config.allowFreeCapture, onFreeCapture]);

  const toggleSource = useCallback((sourceId: string) => {
    if (incompatibleReasons[sourceId]) return;
    setSelectedSourceIds((prev) => {
      if (prev.includes(sourceId)) return prev.filter((x) => x !== sourceId);
      // Sin multi-selección: reemplaza. Con multi: respeta maxSources.
      if (!canMultiSelect) return [sourceId];
      const max = config.multiSelect?.maxSources;
      if (max != null && prev.length >= max) return prev;
      return [...prev, sourceId];
    });
  }, [canMultiSelect, config.multiSelect, incompatibleReasons]);

  const toggleLine = useCallback((lineId: string, selected: boolean) => {
    setSelectedLineIds((prev) => (selected ? [...prev.filter((x) => x !== lineId), lineId] : prev.filter((x) => x !== lineId)));
    if (selected) {
      // Default de cantidad = pendiente.
      const line = pendingLines.find((l) => l.lineId === lineId);
      const pending = line ? Number((line as unknown as Record<string, unknown>)[pendingField] ?? line.quantityPending) : 0;
      setQuantities((prev) => ({ ...prev, [lineId]: prev[lineId] ?? pending }));
      setLineErrors((prev) => {
        const next = { ...prev };
        const err = validateQuantity(pending, pending, line?.unitOfMeasure, i18n, t);
        if (err) next[lineId] = err; else delete next[lineId];
        return next;
      });
    } else {
      setLineErrors((prev) => { const next = { ...prev }; delete next[lineId]; return next; });
    }
  }, [pendingLines, pendingField, i18n, t]);

  const setQuantity = useCallback((lineId: string, quantity: number) => {
    setQuantities((prev) => ({ ...prev, [lineId]: quantity }));
    const line = pendingLines.find((l) => l.lineId === lineId);
    const pending = line ? Number((line as unknown as Record<string, unknown>)[pendingField] ?? line.quantityPending) : 0;
    setLineErrors((prev) => {
      const next = { ...prev };
      const err = validateQuantity(quantity, pending, line?.unitOfMeasure, i18n, t);
      if (err) next[lineId] = err; else delete next[lineId];
      return next;
    });
  }, [pendingLines, pendingField, i18n, t]);

  const setDefault = useCallback((key: string, value: unknown) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setHeaderValue = useCallback((key: string, value: unknown) => {
    setHeaderValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Fetch de líneas al pasar a 'lines' ───────────────────
  const loadLines = useCallback(() => {
    if (!adapter.fetchPendingLines || selectedSourceIds.length === 0) return;
    let cancelled = false;
    setLinesLoading(true);
    setLinesError(undefined);
    adapter
      .fetchPendingLines({ childClass: config.childClass, sourceIds: selectedSourceIds })
      .then((lines) => { if (!cancelled) setPendingLines(lines ?? []); })
      .catch((err) => { if (!cancelled) setLinesError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLinesLoading(false); });
    return () => { cancelled = true; };
  }, [adapter, config.childClass, selectedSourceIds]);

  // ── Navegación ───────────────────────────────────────────
  const canAdvance = useMemo(() => {
    if (step === 'origin') return originChoice === 'source';
    if (step === 'sources') return selectedSourceIds.length > 0;
    if (step === 'lines') {
      return selectedLineIds.length > 0 && selectedLineIds.every((id) => !lineErrors[id] && quantities[id] > 0);
    }
    return true;
  }, [step, originChoice, selectedSourceIds, selectedLineIds, lineErrors, quantities]);

  const buildSources = useCallback((): CreateFromSourceSource[] => {
    const byDoc = new Map<string, CreateFromSourceSource>();
    for (const lineId of selectedLineIds) {
      const line = pendingLines.find((l) => l.lineId === lineId);
      if (!line) continue;
      if (!byDoc.has(line.documentId)) byDoc.set(line.documentId, { documentId: line.documentId, lines: [] });
      byDoc.get(line.documentId)!.lines.push({ sourceLineId: line.lineId, quantityToCross: quantities[lineId] });
    }
    return [...byDoc.values()];
  }, [selectedLineIds, pendingLines, quantities]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const sources = buildSources();
      if (mode === 'create') {
        if (!adapter.createFromSource) throw new Error('adapter.createFromSource no implementado');
        const anchor = candidates.find((c) => c.documentId === selectedSourceIds[0]);
        const result = await adapter.createFromSource({
          childClass: config.childClass,
          header: {
            ...headerValues,                                     // campos createHeaderFields
            operationCenterId,
            // alias nombrados (retrocompatibilidad de contrato):
            referenceNumber: (headerValues.referenceNumber as string) || undefined,
            notes: (headerValues.notes as string) || undefined,
            thirdPartyId: anchor?.thirdPartyId,
          },
          defaults: Object.keys(defaults).length ? defaults : undefined,
          sources,
        });
        onCreated?.(result.documentId);
      } else {
        if (!adapter.appendFromSource || !documentId) throw new Error('adapter.appendFromSource/documentId no disponibles');
        const result = await adapter.appendFromSource(documentId, {
          defaults: Object.keys(defaults).length ? defaults : undefined,
          sources,
        });
        onAppended?.(result.addedLineIds ?? []);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting, buildSources, mode, adapter, candidates, selectedSourceIds, config.childClass,
    operationCenterId, headerValues, defaults, documentId, onCreated, onAppended,
  ]);

  const next = useCallback(() => {
    if (!canAdvance) return;
    if (step === 'origin') { setStep('sources'); return; }
    if (step === 'sources') { setStep('lines'); loadLines(); return; }
    if (step === 'lines') {
      if (hasConfirmStep) setStep('confirm');
      else void submit();
      return;
    }
    if (step === 'confirm') { void submit(); }
  }, [canAdvance, step, hasConfirmStep, loadLines, submit]);

  const back = useCallback(() => {
    if (step === 'confirm') { setStep('lines'); return; }
    if (step === 'lines') { setStep('sources'); return; }
    if (step === 'sources' && hasOriginStep) { setStep('origin'); setSelectedSourceIds([]); return; }
  }, [step, hasOriginStep]);

  return {
    step, originChoice,
    candidates, candidatesLoading, candidatesError, selectedSourceIds, incompatibleReasons,
    pendingLines, linesLoading, linesError, selectedLineIds, quantities, lineErrors,
    defaults, headerValues, selectOptions,
    submitting, submitError,
    canMultiSelect, canFreeCapture, canSeeFinancial,
    canAdvance, hasConfirmStep, hasOriginStep,
    canGoBack: step === 'confirm' || step === 'lines' || (step === 'sources' && hasOriginStep),
    setOriginChoice, toggleSource, toggleLine, setQuantity, setDefault, setHeaderValue,
    next, back, submit,
  };
}
